import {
  Container,
  Grid,
  styled,
  Paper,
  useTheme,
  useMediaQuery,
  Box,
  TextField,
  Button,
} from '@mui/material';
import Page from '../components/Page';
import profilePhoto from '../assets/profile-photo.png';
import { useSelector } from 'react-redux';
import { ChangeEvent, useEffect, useState } from 'react';
import { LoadingButton as LoadingButtonOriginal } from '@mui/lab';
import { ProfileUpdate } from '../features/profile/profileAPI';
import { store } from '../store';
import { getProfile, getProfileCurrent, updateProfile } from '../features/profile/profileSlice';
import { CloudUpload } from '@mui/icons-material';
import VisuallyHiddenInput from '../components/VisuallyHiddenInput';

const LoadingButton = styled(LoadingButtonOriginal)(() => ({
  '&.Mui-disabled': {
    backgroundColor: '#fafafa',
  },
}));

const ProfileWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  padding: '1rem 2rem',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem 1rem',
  },
}));

const InfoWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  marginLeft: '2rem',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    padding: '.5rem 0 .5rem 0',
  },
}));

const Info = styled(Box)(({ theme }) => ({
  borderBottom: '1px solid',
  borderBottomColor: theme.palette.grey[300],
  padding: '.4rem 0',
  width: '100%',
}));

const DataInput = styled(TextField)(() => ({
  marginLeft: '.5rem',
  width: '50%',
  maxWidth: '100%',
}));

export default function Profile() {
  const theme = useTheme();

  const desktop = useMediaQuery(theme.breakpoints.up('md'));

  const profile = useSelector(getProfileCurrent);

  const [edit, setEdit] = useState(false);
  const [data, setData] = useState<ProfileUpdate>({});
  const [currentPhoto, setCurrentPhoto] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    store.dispatch(getProfile())
      .unwrap()
      .catch(err => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!profile) {
      return;
    }

    if (!profile.photo) {
      setCurrentPhoto(profilePhoto);
      return;
    }

    setCurrentPhoto(profile.photo);
  }, [profile]);

  const handleEdit = () => {
    setEdit(true);
  };

  const handleSave = () => {
    setLoading(true);
    store.dispatch(updateProfile(data))
      .unwrap()
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setEdit(false);
        setData({});
        setLoading(false);
      });
  };

  const handleCancel = () => {
    setData({});
    setEdit(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setData({
      ...data,
      [event.target.name]: event.target.value,
    });
  };

  const handlePhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length == 0) {
      setData({
        ...data,
        photo: undefined,
      });
      return;
    }

    const file = event.target.files[0];

    setData({
      ...data,
      photo: file,
    });

    const url = URL.createObjectURL(file);

    setCurrentPhoto(url);
  };

  return (
    <Page>
      <Container>
        <Grid
          container
          padding="1.875rem"
          justifyContent="center"
          direction={desktop ? 'row' : 'column'}
        >
          <Grid mb={2} item sm={8}>
            <ProfileWrapper>
              <Grid container>
                <Grid item xs={12} sm={3} sx={{ textAlign: 'center' }}>
                  <img
                    src={currentPhoto}
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '1rem',
                      marginBottom: '1rem',
                    }}
                  />
                  {
                    edit
                      ? <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
                        Cambiar Foto
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/jpeg,image/png"
                          capture
                          onChange={handlePhoto}
                        />
                      </Button>
                      : null
                  }
                </Grid>

                <Grid item xs={12} sm={9}>
                  <InfoWrapper>
                    <Info>
                      <strong>Nombre y Apellido / Razon Social:</strong>{' '}
                      {
                        edit
                          ? <DataInput
                            name="name"
                            variant="standard"
                            size="small"
                            value={data?.name ?? profile?.name ?? ''}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          : profile && profile.name !== null ? profile.name : 'Nombre'
                      }
                    </Info>
                    <Info>
                      <strong>Teléfono:</strong>{' '}
                      {
                        edit
                          ? <DataInput
                            name="telefono"
                            variant="standard"
                            size="small"
                            value={data?.telefono ?? profile?.telefono ?? ''}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          : profile && profile.telefono ? profile.telefono : 'Teléfono'
                      }
                    </Info>
                    <Info>
                      <strong>Email:</strong>{' '}
                      {
                        edit
                          ? <DataInput
                            name="email"
                            variant="standard"
                            size="small"
                            value={data?.email ?? profile?.email ?? ''}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          : profile && profile.email ? profile.email : 'Email'
                      }
                    </Info>
                    <Info>
                      <strong>Dirección:</strong>{' '}
                      {
                        edit
                          ? <DataInput
                            name="address"
                            variant="standard"
                            size="small"
                            value={data?.address ?? profile?.address ?? ''}
                            onChange={handleChange}
                            disabled={loading}
                          />
                          : profile && profile.address ? profile.address : 'Dirección'
                      }
                    </Info>
                    <Info>
                      <strong>CUIT/CUIL:</strong>{' '}
                      {profile && profile.cuit ? profile.cuit : 'CUIT/CUIL'}
                    </Info>
                  </InfoWrapper>
                </Grid>

                <Grid item xs={12} sx={{ pt: '1rem', textAlign: 'right' }}>
                  {
                    edit
                      ? <LoadingButton loading={loading} variant="outlined" onClick={handleSave}>
                        Guardar
                      </LoadingButton>
                      : <LoadingButton loading={loading} variant="outlined" onClick={handleEdit}>
                        Editar
                      </LoadingButton>
                  }
                  <LoadingButton
                    loading={loading}
                    variant="outlined"
                    sx={{ ml: '1rem' }}
                    disabled={!edit}
                    onClick={handleCancel}
                    color="warning"
                  >
                    Cancelar
                  </LoadingButton>
                </Grid>
              </Grid>
            </ProfileWrapper>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
